import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Button,
  Alert,
  AlertIcon,
  AlertDescription,
  Text,
  Icon,
  FormHelperText,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import { CheckCircleIcon } from "@chakra-ui/icons";

const ConversionModal = ({ isOpen, onClose, place, onUpdate }) => {
  const [formData, setFormData] = useState({
    converted: true,
    conversion_date: new Date().toISOString().split('T')[0],
    conversion_value: 0,
    conversion_notes: "",
    status: "converted",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
  };

  const validateForm = () => {
    if (!formData.conversion_date) {
      setError("Data de conversão é obrigatória");
      return false;
    }
    if (formData.conversion_value < 0) {
      setError("Valor de conversão não pode ser negativo");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      // Garantir que os tipos estejam corretos antes de enviar
      const cleanFormData = {
        ...formData,
        conversion_notes: formData.conversion_notes ? String(formData.conversion_notes).trim() : "",
        conversion_value: Number(formData.conversion_value) || 0,
        conversion_date: String(formData.conversion_date || ""),
      };

      await onUpdate(cleanFormData);
      handleClose();
    } catch (error) {
      setError("Erro ao registrar conversão");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        converted: true,
        conversion_date: new Date().toISOString().split('T')[0],
        conversion_value: 0,
        conversion_notes: "",
        status: "converted",
      });
      setError(null);
      onClose();
    }
  };

  // Data máxima é hoje
  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2}>
            <Icon as={CheckCircleIcon} color="green.500" />
            <Text>Registrar Conversão</Text>
          </HStack>
          <Text fontSize="sm" color="gray.600" mt={1}>
            {place?.title}
          </Text>
        </ModalHeader>
        <ModalCloseButton isDisabled={loading} />

        <ModalBody>
          <VStack spacing={4} align="stretch">
            {error && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Alert status="success" borderRadius="md">
              <AlertIcon />
              <VStack align="start" spacing={1}>
                <Text fontWeight="semibold" fontSize="sm">
                  🎉 Parabéns! Mais uma conversão!
                </Text>
                <AlertDescription fontSize="sm">
                  Registre os detalhes desta conversão para acompanhar
                  o desempenho da sua prospecção.
                </AlertDescription>
              </VStack>
            </Alert>

            <FormControl isRequired>
              <FormLabel>Data da Conversão</FormLabel>
              <Input
                type="date"
                value={formData.conversion_date}
                onChange={(e) => handleInputChange("conversion_date", e.target.value)}
                isDisabled={loading}
                max={getTodayDate()}
              />
              <FormHelperText>
                Quando a conversão foi concretizada?
              </FormHelperText>
            </FormControl>

            <FormControl>
              <FormLabel>Valor da Conversão</FormLabel>
              <InputGroup>
                <InputLeftElement
                  pointerEvents="none"
                  color="gray.300"
                  fontSize="1.2em"
                  children="R$"
                />
                <NumberInput
                  value={formData.conversion_value}
                  onChange={(_, value) => handleInputChange("conversion_value", value || 0)}
                  min={0}
                  precision={2}
                  step={0.01}
                  isDisabled={loading}
                >
                  <NumberInputField pl="40px" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </InputGroup>
              <FormHelperText>
                Valor aproximado do negócio fechado (deixe 0 se não souber)
              </FormHelperText>
            </FormControl>

            <FormControl>
              <FormLabel>Observações da Conversão</FormLabel>
              <Textarea
                placeholder="Descreva o que foi fechado, tipo de contrato, próximos passos..."
                value={formData.conversion_notes}
                onChange={(e) => handleInputChange("conversion_notes", e.target.value)}
                isDisabled={loading}
                rows={4}
              />
              <FormHelperText>
                Registre detalhes importantes sobre o que foi acordado
              </FormHelperText>
            </FormControl>

            <Alert status="info" borderRadius="md" size="sm">
              <AlertIcon />
              <AlertDescription fontSize="sm">
                <strong>Não se esqueça:</strong>
                <br />
                • Envie contrato ou proposta final
                <br />
                • Configure o cliente no sistema
                <br />
                • Programe follow-up pós-venda
                <br />
                • Celebre esta conquista! 🎉
              </AlertDescription>
            </Alert>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button
              variant="ghost"
              onClick={handleClose}
              isDisabled={loading}
            >
              Cancelar
            </Button>
            <Button
              colorScheme="green"
              onClick={handleSubmit}
              isLoading={loading}
              loadingText="Registrando..."
              leftIcon={<CheckCircleIcon />}
            >
              Registrar Conversão
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ConversionModal;